import React, {
    createContext,
    useContext,
    useCallback,
    useState,
    useMemo,
} from "react";
import type {
    TransmissionContextState,
    TransmissionContextValue,
    VisibleFeature,
} from "../types/types";
import {
    isTransmissionLineFeature,
    transformTransmissionLineFeature,
    TransmissionLineDisplay,
    TransmissionLineFeature,
} from "../component/map/TransmissionLineLayerColumns";
import {
    isSubstationFeature,
    transformSubstationFeature,
    SubstationDisplay,
    SubstationFeature,
} from "../component/map/SubstationLayerColumns";
import {
    isGeneratorFeature,
    transformGeneratorFeature,
    GeneratorDisplay,
    GeneratorFeature,
} from "../component/map/GeneratorLayerColumns";

export type TableFilterOptions = "all" | "selected" | "visible";

type FeatureTypeMap = {
    transmissionLine: {
        feature: TransmissionLineFeature;
        display: TransmissionLineDisplay;
        check: (feature: VisibleFeature) => feature is TransmissionLineFeature;
        transform: (
            feature: TransmissionLineFeature
        ) => TransmissionLineDisplay;
    };
    substation: {
        feature: SubstationFeature;
        display: SubstationDisplay;
        check: (feature: VisibleFeature) => feature is SubstationFeature;
        transform: (feature: SubstationFeature) => SubstationDisplay;
    };
    generator: {
        feature: GeneratorFeature;
        display: GeneratorDisplay;
        check: (feature: VisibleFeature) => feature is GeneratorFeature;
        transform: (feature: GeneratorFeature) => GeneratorDisplay;
    };
};

const featureHandlers: {
    [K in keyof FeatureTypeMap]: {
        check: FeatureTypeMap[K]["check"];
        transform: FeatureTypeMap[K]["transform"];
    };
} = {
    transmissionLine: {
        check: isTransmissionLineFeature,
        transform: transformTransmissionLineFeature,
    },
    substation: {
        check: isSubstationFeature,
        transform: transformSubstationFeature,
    },
    generator: {
        check: isGeneratorFeature,
        transform: transformGeneratorFeature,
    },
} as const;

const TransmissionInsightsContext = createContext<
    TransmissionContextValue | undefined
>(undefined);

export const TransmissionInsightsProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [state, setState] = useState<TransmissionContextState>({
        visibleFeatures: [],
        selectedArea: {
            bounds: null,
            features: [],
        },
    });

    const updateVisibleFeatures = useCallback((features: VisibleFeature[]) => {
        setState((prev) => ({
            ...prev,
            visibleFeatures: features,
        }));
    }, []);

    const updateSelectedArea = useCallback(
        (
            bounds: [[number, number], [number, number]] | null,
            features: VisibleFeature[]
        ) => {
            setState((prev) => ({
                ...prev,
                selectedArea: {
                    bounds,
                    features,
                },
            }));
        },
        []
    );

    const clearSelectedArea = useCallback(() => {
        setState((prev) => ({
            ...prev,
            selectedArea: {
                bounds: null,
                features: [],
            },
        }));
    }, []);

    const value: TransmissionContextValue = {
        ...state,
        updateVisibleFeatures,
        updateSelectedArea,
        clearSelectedArea,
    };

    return (
        <TransmissionInsightsContext.Provider value={value}>
            {children}
        </TransmissionInsightsContext.Provider>
    );
};

export const useTransmissionInsights = () => {
    const context = useContext(TransmissionInsightsContext);
    if (context === undefined) {
        throw new Error(
            "useTransmissionInsights must be used within a TransmissionInsightsProvider"
        );
    }
    return context;
};

// Hook for querying visible features
export const useVisibleFeatures = () => {
    const { visibleFeatures } = useTransmissionInsights();

    const getFeaturesByType = useCallback(
        (type: VisibleFeature["type"]) => {
            return visibleFeatures.filter((feature) => feature.type === type);
        },
        [visibleFeatures]
    );

    const getFeaturesByArea = useCallback(
        (bounds: [[number, number], [number, number]]) => {
            return visibleFeatures.filter((feature) => {
                const [lng, lat] = feature.geometry.coordinates as [
                    number,
                    number,
                ];
                const [[minLng, minLat], [maxLng, maxLat]] = bounds;
                return (
                    lng >= minLng &&
                    lng <= maxLng &&
                    lat >= minLat &&
                    lat <= maxLat
                );
            });
        },
        [visibleFeatures]
    );

    return {
        allFeatures: visibleFeatures,
        getFeaturesByType,
        getFeaturesByArea,
    };
};

export function useFeatureData<T extends keyof FeatureTypeMap>(
    featureType: T,
    filterOption: TableFilterOptions = "visible"
): FeatureTypeMap[T]["display"][] {
    const { visibleFeatures, selectedArea } = useTransmissionInsights();

    return useMemo(() => {
        let features = [];

        switch (filterOption) {
            case "all":
            case "visible":
                features = visibleFeatures;
                break;
            case "selected":
                features = selectedArea.features;
                break;
            default:
                features = visibleFeatures;
        }

        const { check, transform } = featureHandlers[featureType];

        const checkedFeatures = features.filter(check) as Array<
            FeatureTypeMap[T]["feature"]
        >;
        return checkedFeatures.map((feature: any) => transform(feature));
    }, [featureType, filterOption, visibleFeatures, selectedArea.features]);
}

export const useTransmissionLineData = (
    filterOption: TableFilterOptions = "visible"
) => useFeatureData("transmissionLine", filterOption);

export const useSubstationData = (
    filterOption: TableFilterOptions = "visible"
) => useFeatureData("substation", filterOption);

export const useGeneratorData = (
    filterOption: TableFilterOptions = "visible"
) => useFeatureData("generator", filterOption);